// @flow

import { LOCATION_CHANGE } from "connected-react-router";
import medicalDetails from "./medical-details/state";
import employmentDetails from "./employment-details/state";
import userAccount from "./user-account-details/state";
import {
  AddMedicalHistoryItem,
  AddMedicalHistoryItemSuccess,
  AddMedicalHistoryItemError,
  ResetActiveClients,
  GetActiveClients,
  GetActiveClientsSuccess,
  GetActiveClientsError,
  GetEmployees,
  SetEmployeesDefaultData,
  SelectBespokeRecipient,
  GetEmployeesSuccess,
  GetEmployee,
  GetEmployeeSuccess,
  GetEmployeesError,
  GetCurrentEmployeeSuccess,
  SetEmployeesSort,
  AddEmployee,
  GetEmployeeActionSummary,
  GetEmployeeActionSummarySuccess,
  GetEmployeeSchemaSuccess,
  GetEmployeeSchemaError,
  GetEmployeeSchema,
  ToggleAddAllergyModal,
  ToggleAddMedicationModal,
  ToggleAddMedicalHistoryItemModal,
  ToggleRemoveMedicationModal,
  ToggleRemoveAllergyModal,
  GetEmployeeMedicationsSuccess,
  SetMedicationSchema,
  GetEmployeeAllergiesSuccess,
  GetEmployeeVaccinations,
  GetEmployeeVaccinationsSuccess,
  GetEmployeeVaccinationsError,
  SetAllergySchema,
  SetMedicalHistorySchema,
  GetEmployeeMedicalHistory,
  GetEmployeeMedicalHistorySuccess,
  GetEmployeeMedicationsError,
  GetEmployeeAllergiesError,
  GetEmployeeMedicalHistoryError,
  GetMedicalDetailsChangelogSuccess,
  GetMedicalDetailsChangelogError,
  GetAddMedicationError,
  GetRemoveMedicationError,
  GetAddAllergyError,
  GetRemoveAllergyError,
  GetAddMedicalHistoryItemError,
  GetEmployeeRoleSummary,
  GetEmployeeRoleSummarySuccess,
  GetEmployeeRoleSummaryError,
  GetEmployeePositionSchemaSuccess,
  GetEmployeePositionSchemaError,
  GetEmployeePositionChangelog,
  GetEmployeePositionChangelogSuccess,
  GetEmployeePositionChangelogError,
  GetEmployeeSpecialContacts,
  GetEmployeeSpecialContactsSuccess,
  GetEmployeeSpecialContactsError,
  ToggleDeleteEmployeeSpecialContactsModal,
  ToggleAddEmployeePositionModal,
  ToggleEditEmployeePositionModal,
  ToggleUserAccountAssignmentsModal,
  ToggleEditResetPasswordModal,
  ToggleConfirmResetPasswordModal,
  ToggleEditAccessLevelModal,
  SetEmployeeMedicationSort,
  SetEmployeeAllergySort,
  SetEmployeeMedicalHistorySort,
  TogglePersonalDetailsModal,
  UpdateEmployeeDetailsError,
  UpdateEmployeePositionError,
  UpdateEmployeePosition,
  UpdateEmployeePositionSuccess,
  UpdateMedication,
  UpdateMedicationSuccess,
  UpdateMedicationError,
  UpdateAllergy,
  UpdateAllergySuccess,
  UpdateAllergyError,
  GetEmployeesSecurityQuestionsSuccess,
  GetEmployeesSecurityQuestionsError,
  GetUserRecentLogins,
  GetUserRecentLoginsSuccess,
  GetUserRecentLoginsError,
  GetUserAccountChangelog,
  GetUserAccountChangelogSuccess,
  GetUserAccountChangelogError,
  GetAddEmployeeSchemaSuccess,
  GetAddEmployeeSchemaError,
  SetShowExcludeEmployeeFromAlertsAddModal,
  GetEmployeeAlertExclusions,
  GetEmployeeAlertExclusionsError,
  GetEmployeeAlertExclusionsSuccess,
  SetShowRemoveAlertExclusionModal,
  SetEmployeeAlertExclusionSchema,
  GetClientAlertSuccess,
  SetExclusionReasonDetails,
  ClearDataAlertExlusionForm,
  SetShowEditAlertExclusionModal,
  GetAccessLevelSchemaSuccess,
  GetAccessLevelValueSuccess,
  UpdateAccessLevel,
  UpdateAccessLevelSuccess,
  UpdateAccessLevelError,
  GetUserAccountAssignmentsSuccess,
  GetUserAccountAssignmentsError,
  UpdateEmployeeDraft,
  ClearEmployeeDraft,
  ChangeEmployeeFilterInitialValues,
  ToggleUserAccountImpersonateModal,
  GetSavedReportsSuccess,
  GetReportLogDataSuccess,
  ToggleLogDataModal,
  GetEmployeeReferralHistory,
  GetEmployeeReferralHistorySuccess,
  GetEmployeeReferralHistoryError,
  ToggleAddVaccinationModal,
  ToggleDeleteVaccinationModal,
  GetVaccineTypesSuccess,
  ToggleRemoveMedicalHistoryItemModal,
  AddMedication,
  AddMedicationSuccess,
  AddMedicationError,
  AddAllergy,
  AddAllergySuccess,
  AddAllergyError,
  AddEmployeeVaccination,
  AddEmployeeVaccinationSuccess,
  AddEmployeeVaccinationError,
  RemoveMedication,
  RemoveMedicationSuccess,
  RemoveMedicationError,
  RemoveAllergy,
  RemoveAllergySuccess,
  RemoveAllergyError,
  RemoveMedicalHistoryItem,
  RemoveMedicalHistoryItemSuccess,
  RemoveMedicalHistoryItemError,
  DeleteEmployeeVaccination,
  DeleteEmployeeVaccinationSuccess,
  DeleteEmployeeVaccinationError,
  ExportEmployeeReferralHistory,
  ExportEmployeeReferralHistorySuccess,
  ExportEmployeeReferralHistoryError,
  UpdateUserAccountAssignments,
  UpdateUserAccountAssignmentsSuccess,
  UpdateUserAccountAssignmentsError,
} from "@modules/employee/constants/actions";
import employee from "./employee/state";
import clients from "./clients/state";
import { initialState as personalDetailsState } from "./personal-details/state";
import * as clientsReducers from "@modules/employee/reducer/clients/reducers";
import * as employeesReducers from "@modules/employee/reducer/employee/reducers";
import * as employmentDetailsReducers from "@modules/employee/reducer/employment-details/reducers";
import * as medicalDetailsReducers from "@modules/employee/reducer/medical-details/reducers";
import * as personalDetailsReducers from "@modules/employee/reducer/personal-details/reducers";
import * as userAccountReducers from "@modules/employee/reducer/user-account-details/reducers";
import type { PersonalDetailsState } from "@modules/employee/reducer/personal-details/types";
import type { MedicalDetailsState } from "@modules/employee/reducer/medical-details/types";
import type { EmploymentDetailsState } from "@modules/employee/reducer/employment-details/types";
import type { UserAccountsDetailsState } from "@modules/employee/reducer/user-account-details/types";
import type { ClientsState } from "@modules/employee/reducer/clients/types";
import type { EmployeeState } from "@modules/employee/reducer/employee/types";
import { cloneObject } from "@utils/object";

export type EmployeesState = $Exact<{
  error: string | null,
  success: string | null,
  isLoading: boolean,
  employee: $Exact<{
    ...EmployeeState,
    ...PersonalDetailsState,
    medicalDetails: MedicalDetailsState,
    employmentDetails: EmploymentDetailsState,
    userAccount: UserAccountsDetailsState,
  }>,
  clients: ClientsState,
}>;

export const initialState: EmployeesState = {
  error: null,
  success: null,
  isLoading: false,
  employee: {
    ...employee,
    ...personalDetailsState,
    medicalDetails,
    employmentDetails,
    userAccount,
  },
  clients,
};

const onLocationChange = (state: EmployeesState) => () => {
  return {
    ...state,
    isLoading: null,
    error: null,
  };
};

export default (state: EmployeesState = initialState, action: Object) => {
  const act = {
    [ToggleAddVaccinationModal]: medicalDetailsReducers.onToggleAddVaccinationModal,
    [ToggleDeleteVaccinationModal]: medicalDetailsReducers.onToggleDeleteVaccinationModal,
    [GetVaccineTypesSuccess]: medicalDetailsReducers.onGetVaccineTypesSuccess, // Employee medical details
    [ToggleAddMedicationModal]: medicalDetailsReducers.onToggleAddMedicationModal,
    [ToggleAddAllergyModal]: medicalDetailsReducers.onToggleAddAllergyModal,
    [AddMedication]: medicalDetailsReducers.setLoading,
    [AddMedicationSuccess]: medicalDetailsReducers.setLoaded,
    [AddMedicationError]: medicalDetailsReducers.setLoaded,
    [AddAllergy]: medicalDetailsReducers.setLoading,
    [AddAllergySuccess]: medicalDetailsReducers.setLoaded,
    [AddAllergyError]: medicalDetailsReducers.setLoaded,
    [RemoveMedication]: medicalDetailsReducers.setLoading,
    [RemoveMedicationSuccess]: medicalDetailsReducers.setLoaded,
    [RemoveMedicationError]: medicalDetailsReducers.setLoaded,
    [RemoveAllergy]: medicalDetailsReducers.setLoading,
    [RemoveAllergySuccess]: medicalDetailsReducers.setLoaded,
    [RemoveAllergyError]: medicalDetailsReducers.setLoaded,
    [RemoveMedicalHistoryItem]: medicalDetailsReducers.setLoading,
    [RemoveMedicalHistoryItemSuccess]: medicalDetailsReducers.setLoaded,
    [RemoveMedicalHistoryItemError]: medicalDetailsReducers.setLoaded,
    [DeleteEmployeeVaccination]: medicalDetailsReducers.setLoading,
    [DeleteEmployeeVaccinationSuccess]: medicalDetailsReducers.setLoaded,
    [DeleteEmployeeVaccinationError]: medicalDetailsReducers.setLoaded,
    [ToggleAddMedicalHistoryItemModal]: medicalDetailsReducers.onToggleAddMedicalHistoryItemModal, // Personal detail
    [GetEmployeeActionSummary]: personalDetailsReducers.setLoading,
    [GetEmployeeActionSummarySuccess]: personalDetailsReducers.onGetEmployeeActionSummarySuccess,
    [GetEmployee]: personalDetailsReducers.setLoading,
    [GetEmployeeSuccess]: personalDetailsReducers.onGetEmployeeSuccess,
    [GetEmployeeSchemaSuccess]: personalDetailsReducers.onGetEmployeeSchemaSuccess,
    [GetEmployeeSchema]: personalDetailsReducers.setLoading,
    [TogglePersonalDetailsModal]: personalDetailsReducers.onTogglePersonalDetailsModal,
    [UpdateEmployeeDetailsError]: personalDetailsReducers.onUpdateEmployeeDetailsError,
    [GetAddEmployeeSchemaSuccess]: personalDetailsReducers.onGetAddEmployeeSchemaSuccess,
    [GetAddEmployeeSchemaError]: personalDetailsReducers.onGetAddEmployeeSchemaError,
    [SetShowExcludeEmployeeFromAlertsAddModal]:
      personalDetailsReducers.onSetShowExcludeEmployeeFromAlertsAddModal,
    [GetEmployeeAlertExclusions]: personalDetailsReducers.onGetEmployeeAlertExclusions,
    [GetEmployeeAlertExclusionsSuccess]:
      personalDetailsReducers.onGetEmployeeAlertExclusionsSuccess,
    [GetEmployeeAlertExclusionsError]: personalDetailsReducers.onGetEmployeeAlertExclusionsError,
    [SetShowRemoveAlertExclusionModal]: personalDetailsReducers.onSetShowRemoveAlertExclusionModal,
    [SetShowEditAlertExclusionModal]: personalDetailsReducers.onSetShowEditAlertExclusionModal,
    [SetEmployeeAlertExclusionSchema]: personalDetailsReducers.onSetEmployeeAlertExclusionSchema,
    [GetClientAlertSuccess]: personalDetailsReducers.onGetClientAlertSuccess,
    [SetExclusionReasonDetails]: personalDetailsReducers.onSetExclusionReasonDetails,
    [ClearDataAlertExlusionForm]: personalDetailsReducers.onClearDataAlertExlusionForm,

    // Medical Details
    [ToggleRemoveMedicationModal]: medicalDetailsReducers.onToggleRemoveMedicationModal,
    [ToggleRemoveAllergyModal]: medicalDetailsReducers.onToggleRemoveAllergyModal,
    [GetEmployeeMedicationsSuccess]: medicalDetailsReducers.onGetEmployeeMedicationsSuccess,
    [SetMedicationSchema]: medicalDetailsReducers.onSetMedicationSchema,
    [GetEmployeeAllergiesSuccess]: medicalDetailsReducers.onGetEmployeeAllergiesSuccess,
    [GetEmployeeVaccinations]: medicalDetailsReducers.onGetEmployeeVaccinations,
    [GetEmployeeVaccinationsSuccess]: medicalDetailsReducers.onGetEmployeeVaccinationsSuccess,
    [GetEmployeeVaccinationsError]: medicalDetailsReducers.onGetEmployeeVaccinationsError,
    [SetAllergySchema]: medicalDetailsReducers.onSetAllergySchema,
    [SetMedicalHistorySchema]: medicalDetailsReducers.onSetMedicalHistorySchema,
    [GetEmployeeMedicalHistory]: medicalDetailsReducers.setLoading,
    [GetEmployeeMedicalHistorySuccess]: medicalDetailsReducers.onGetEmployeeMedicalHistorySuccess,
    [GetEmployeeMedicationsError]: medicalDetailsReducers.onGetEmployeeMedicationsError,
    [GetEmployeeAllergiesError]: medicalDetailsReducers.onGetEmployeeAllergiesError,
    [GetEmployeeMedicalHistoryError]: medicalDetailsReducers.onGetEmployeeMedicalHistoryError,
    [GetAddMedicationError]: medicalDetailsReducers.onGetAddMedicationError,
    [GetRemoveMedicationError]: medicalDetailsReducers.onGetRemoveMedicationError,
    [GetAddAllergyError]: medicalDetailsReducers.onGetAddAllergyError,
    [GetRemoveAllergyError]: medicalDetailsReducers.onGetRemoveAllergyError,
    [GetAddMedicalHistoryItemError]: medicalDetailsReducers.onGetAddMedicalHistoryItemError,
    [GetEmployeeSchemaError]: personalDetailsReducers.onGetEmployeeSchemaError,
    [GetMedicalDetailsChangelogSuccess]: medicalDetailsReducers.onGetMedicalDetailsChangelogSuccess,
    [GetMedicalDetailsChangelogError]: medicalDetailsReducers.onGetMedicalDetailsChangelogError,
    [AddMedicalHistoryItem]: medicalDetailsReducers.setLoading,
    [AddMedicalHistoryItemSuccess]: medicalDetailsReducers.setLoaded,
    [AddMedicalHistoryItemError]: medicalDetailsReducers.setLoaded,
    [AddEmployeeVaccination]: medicalDetailsReducers.setLoading,
    [AddEmployeeVaccinationSuccess]: medicalDetailsReducers.setLoaded,
    [AddEmployeeVaccinationError]: medicalDetailsReducers.setLoaded,
    [SetEmployeeMedicationSort]: medicalDetailsReducers.onSetEmployeeMedicationSort,
    [SetEmployeeAllergySort]: medicalDetailsReducers.onSetEmployeeAllergySort,
    [SetEmployeeMedicalHistorySort]: medicalDetailsReducers.onSetEmployeeMedicalHistorySort,
    [UpdateMedication]: medicalDetailsReducers.setLoading,
    [UpdateMedicationSuccess]: medicalDetailsReducers.setLoaded,
    [UpdateMedicationError]: medicalDetailsReducers.setLoaded,
    [UpdateAllergy]: medicalDetailsReducers.setLoading,
    [UpdateAllergySuccess]: medicalDetailsReducers.setLoaded,
    [UpdateAllergyError]: medicalDetailsReducers.setLoaded,
    [ToggleRemoveMedicalHistoryItemModal]:
      medicalDetailsReducers.onToggleRemoveMedicalHistoryItemModal,

    //Employee Search
    [GetEmployeeReferralHistory]: employeesReducers.setLoading,
    [GetEmployeeReferralHistoryError]: employeesReducers.stopLoading,
    [GetEmployeeReferralHistorySuccess]: employeesReducers.onGetEmployeeReferralHistorySuccess,
    [ResetActiveClients]: clientsReducers.onResetActiveClients,
    [GetActiveClients]: clientsReducers.onGetActiveClients,
    [GetActiveClientsSuccess]: clientsReducers.onGetActiveClientsSuccess,
    [GetActiveClientsError]: clientsReducers.onGetActiveClientsError,
    [GetEmployees]: employeesReducers.setLoading,
    [SetEmployeesDefaultData]: employeesReducers.onSetEmployeesDefaultData,
    [SelectBespokeRecipient]: employeesReducers.onSelectBespokeRecipient,
    [GetEmployeesSuccess]: employeesReducers.onGetEmployeesSuccess,
    [GetEmployeesError]: employeesReducers.onGetEmployeesError,
    [GetCurrentEmployeeSuccess]: employeesReducers.onGetCurrentEmployeeSuccess,
    [GetEmployeesSecurityQuestionsSuccess]:
      employeesReducers.onGetEmployeesSecurityQuestionsSuccess,
    [GetEmployeesSecurityQuestionsError]: employeesReducers.onGetEmployeesSecurityQuestionsError,
    [AddEmployee]: employeesReducers.onAddEmployee,
    [SetEmployeesSort]: employeesReducers.onSetEmployeesSort,

    [UpdateEmployeeDraft]: employeesReducers.onUpdateEmployeeDraft,
    [ClearEmployeeDraft]: employeesReducers.onClearEmployeeDraft,

    [ExportEmployeeReferralHistory]: employeesReducers.setLoading,
    [ExportEmployeeReferralHistorySuccess]: employeesReducers.stopLoading,
    [ExportEmployeeReferralHistoryError]: employeesReducers.stopLoading,

    //Employment Details
    [GetEmployeeRoleSummary]: employmentDetailsReducers.setLoading,
    [GetEmployeeRoleSummarySuccess]: employmentDetailsReducers.onGetEmployeeRoleSummarySuccess,
    [GetEmployeeRoleSummaryError]: employmentDetailsReducers.onGetEmployeeRoleSummaryError,
    [GetEmployeePositionSchemaSuccess]:
      employmentDetailsReducers.onGetEmployeePositionSchemaSuccess,
    [GetEmployeePositionSchemaError]: employmentDetailsReducers.onGetEmployeePositionSchemaError,
    [ToggleAddEmployeePositionModal]: employmentDetailsReducers.onToggleAddEmployeePositionModal,
    [ToggleEditEmployeePositionModal]: employmentDetailsReducers.onToggleEditEmployeePositionModal,
    [GetEmployeePositionChangelog]: employmentDetailsReducers.setLoading,
    [GetEmployeePositionChangelogSuccess]:
      employmentDetailsReducers.onGetEmployeePositionChangelogSuccess,
    [GetEmployeePositionChangelogError]:
      employmentDetailsReducers.onGetEmployeePositionChangelogError,
    [GetEmployeeSpecialContacts]: employmentDetailsReducers.setLoading,
    [GetEmployeeSpecialContactsSuccess]:
      employmentDetailsReducers.onGetEmployeeSpecialContactsSuccess,
    [GetEmployeeSpecialContactsError]: employmentDetailsReducers.onGetEmployeeSpecialContactsError,
    [ToggleDeleteEmployeeSpecialContactsModal]:
      employmentDetailsReducers.onToggleDeleteEmployeeSpecialContactsModal,
    [UpdateEmployeePositionError]: employmentDetailsReducers.onUpdateEmployeePositionError,
    [UpdateEmployeePosition]: employmentDetailsReducers.setLoading,
    [UpdateEmployeePositionSuccess]: employmentDetailsReducers.onUpdateEmployeePositionSuccess,

    [LOCATION_CHANGE]: onLocationChange,

    //User Account Details
    [ToggleEditAccessLevelModal]: userAccountReducers.onToggleEditAccessLevelModal,
    [ToggleEditResetPasswordModal]: userAccountReducers.onToggleEditResetPasswordModal,
    [ToggleConfirmResetPasswordModal]: userAccountReducers.onToggleConfirmResetPasswordModal,
    [GetUserRecentLogins]: userAccountReducers.setLoading,
    [GetUserRecentLoginsSuccess]: userAccountReducers.onGetUserRecentLoginsSuccess,
    [GetUserRecentLoginsError]: userAccountReducers.onGetUserRecentLoginsError,
    [GetUserAccountChangelog]: userAccountReducers.setLoading,
    [GetUserAccountChangelogSuccess]: userAccountReducers.onGetUserAccountChangelogSuccess,
    [GetUserAccountChangelogError]: userAccountReducers.onGetUserAccountChangelogError,

    [GetAccessLevelSchemaSuccess]: userAccountReducers.onGetAccessLevelSchemaSuccess,
    [GetAccessLevelValueSuccess]: userAccountReducers.onGetAccessLevelValueSuccess,

    [UpdateAccessLevel]: userAccountReducers.setLoading,
    [UpdateAccessLevelSuccess]: userAccountReducers.onUpdateAccessLevelSuccess,
    [UpdateAccessLevelError]: userAccountReducers.setLoaded,

    [UpdateUserAccountAssignments]: userAccountReducers.setLoading,
    [UpdateUserAccountAssignmentsSuccess]: userAccountReducers.setLoaded,
    [UpdateUserAccountAssignmentsError]: userAccountReducers.setLoaded,

    [GetUserAccountAssignmentsSuccess]: userAccountReducers.onGetUserAccountAssignmentsSuccess,
    [GetUserAccountAssignmentsError]: userAccountReducers.onGetUserAccountAssignmentsError,
    [ToggleUserAccountAssignmentsModal]: userAccountReducers.onToggleUserAccountAssignmentsModal,
    [ChangeEmployeeFilterInitialValues]: employeesReducers.onChangeEmployeeFilterInitialValues,
    [ToggleUserAccountImpersonateModal]: userAccountReducers.onToggleUserAccountImpersonateModal,
    [GetSavedReportsSuccess]: userAccountReducers.onGetSavedReportsSuccess,

    [ToggleLogDataModal]: userAccountReducers.onToggleLogDataModal,
    [GetReportLogDataSuccess]: userAccountReducers.onGetReportLogDataSuccess,
  };
  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
